import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, X, Clock, Loader2 } from 'lucide-react';

type Status = 'loading' | 'contact' | 'newsletter' | 'expired' | 'invalid' | 'error';

const ConfirmPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState<Status>('loading');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    const run = async () => {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !supabaseKey) {
        setStatus('error');
        return;
      }
      try {
        const res = await fetch(`${supabaseUrl}/functions/v1/confirm-submission`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (!res.ok) {
          setStatus('error');
          return;
        }
        if (data.status === 'confirmed' && data.type === 'contact') {
          setStatus('contact');
          setTimeout(() => {
            navigate('/contact?confirmed=1', { replace: true });
          }, 1200);
          return;
        }
        else if (data.status === 'confirmed' && data.type === 'newsletter') setStatus('newsletter');
        else if (data.status === 'expired') setStatus('expired');
        else setStatus('invalid');
      } catch {
        setStatus('error');
      }
    };
    run();
  }, [token, navigate]);

  const content = (() => {
    switch (status) {
      case 'loading':
        return {
          icon: <Loader2 className="h-6 w-6 text-white/70 animate-spin" />,
          ring: 'bg-white/[0.04] border-white/10',
          title: 'Confirming...',
          body: 'Just a moment while we verify your link.',
        };
      case 'contact':
        return {
          icon: <Check className="h-6 w-6 text-emerald-400" />,
          ring: 'bg-emerald-500/10 border-emerald-500/20',
          title: 'Message confirmed',
          body: "Thanks for confirming. Your message is on its way to us and we'll be in touch within 24 hours.",
        };
      case 'newsletter':
        return {
          icon: <Check className="h-6 w-6 text-emerald-400" />,
          ring: 'bg-emerald-500/10 border-emerald-500/20',
          title: "You're on the list",
          body: "Subscription confirmed. We'll be in touch when we've got something worth sharing.",
        };
      case 'expired':
        return {
          icon: <Clock className="h-6 w-6 text-amber-400" />,
          ring: 'bg-amber-500/10 border-amber-500/20',
          title: 'Link expired',
          body: 'This confirmation link is more than 24 hours old. Please submit the form again to get a fresh link.',
        };
      case 'invalid':
        return {
          icon: <X className="h-6 w-6 text-rose-400" />,
          ring: 'bg-rose-500/10 border-rose-500/20',
          title: 'Link not valid',
          body: 'This confirmation link is invalid or has already been used. Please submit the form again if you need to.',
        };
      case 'error':
      default:
        return {
          icon: <X className="h-6 w-6 text-rose-400" />,
          ring: 'bg-rose-500/10 border-rose-500/20',
          title: 'Something went wrong',
          body: "We couldn't confirm right now. Please try the link again in a few minutes or contact us directly.",
        };
    }
  })();

  return (
    <section className="min-h-screen pt-24 lg:pt-28">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="relative p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] overflow-hidden text-center">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
          <div className="relative z-10">
            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl border mb-6 ${content.ring}`}>
              {content.icon}
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-white mb-4">{content.title}</h1>
            <p className="text-gray-400 max-w-md mx-auto leading-relaxed">{content.body}</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-center">
              {status === 'contact' && (
                <Link to="/contact?confirmed=1" className="btn-primary inline-flex items-center">
                  Continue
                </Link>
              )}
              <Link to="/" className="btn-secondary inline-flex items-center">
                Back to homepage
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConfirmPage;
